<script setup lang="ts">
import type { QuantizedReelFrameSize } from './quantizeReelFrameSize';
import type {
	PropertyReelsCloseReason,
	PropertyReelsFeedEmits,
	PropertyReelsFeedProps,
	ReelItemProperty
} from './types';
import type { CarouselApi } from '~/components/ui/carousel';

import {
	breakpointsTailwind,
	useBreakpoints,
	useDebounceFn,
	useEventListener,
	useResizeObserver,
	useScrollLock,
	useTimeoutFn
} from '@vueuse/core';
import { getSavedBandwidthEstimate } from '~/composables/useHlsPlayer/shakaLoader';

import { quantizeReelFrameSize } from './quantizeReelFrameSize';
import ShortsDesktopActions from './ShortsDesktopActions.vue';
import ShortsVideoFeed from './ShortsVideoFeed.vue';
import { isIOSDevice, provideIOSSharedVideo } from './useIOSSharedVideo';
import { provideShortsEntry } from './useShortsEntryContext';
import { useShortsPageLifecycle } from './useShortsPageLifecycle';
import { useShortsPrefetch } from './useShortsPrefetch';
import { useShortsVideoState } from './useShortsVideoState';

// Background image is always blur-2xl — at that level of blur, quality
// differences are invisible. Scale down aggressively to save bandwidth.
const bgImageQuality = computed(() => {
	if (!import.meta.client) return 70;
	const bps = getSavedBandwidthEstimate();
	if (bps === null || bps >= 4_000_000) return 75;
	if (bps >= 2_000_000) return 70;
	return 60;
});

const props = withDefaults(defineProps<PropertyReelsFeedProps>(), {
	initialIndex: 0,
	initialMuted: true,
	preloadFirst: false,
	videoType: 'native',
	isLoading: false,
	isReady: true,
	hasMore: false,
	hasMoreHistory: false,
	historyCount: 0,
	showNavigation: false,
	showExploreButton: false,
	isPaused: false,
	disableScroll: false,
	emptyStateTitle: 'Nenhum vídeo encontrado',
	emptyStateDescription: 'Ajuste os filtros para ver mais resultados',
	hideHeaderControls: false,
	disableKeyboard: false,
	shortsEntry: undefined,
	hasLeadingItem: false,
	autoplayArmed: true
});

const emit = defineEmits<PropertyReelsFeedEmits>();

provideShortsEntry(() => props.shortsEntry);

const isOpen = defineModel<boolean>({ default: false });

const currentBgImageUrl = ref<string | null>(null);

function handleCarouselImageChange(url: string | null) {
	currentBgImageUrl.value = url;
}

const bodyElement = computed(() => (import.meta.client ? document.body : null));
const isScrollLocked = useScrollLock(bodyElement);

const emblaApi = ref<CarouselApi | null>(null);
const currentIndex = ref(props.initialIndex);
const emblaListeners = ref<Array<{ event: any; callback: any }>>([]);

const hasLeadingItem = computed(() => props.hasLeadingItem);
const leadingItemCount = computed(() => (hasLeadingItem.value ? 1 : 0));
const totalItems = computed(() => (props.items || []).length);
const totalSlides = computed(() => totalItems.value + leadingItemCount.value);
const currentItemIndex = computed(
	() => currentIndex.value - leadingItemCount.value
);
const currentItem = computed(() =>
	currentItemIndex.value >= 0
		? (props.items[currentItemIndex.value] ?? null)
		: null
);
const isLastItem = computed(
	() => currentIndex.value === totalSlides.value - 1 && !props.hasMore
);
const canGoPrevious = computed(
	() =>
		totalSlides.value > 0 && (currentIndex.value > 0 || props.hasMoreHistory)
);
const canGoNext = computed(
	() => totalSlides.value > 0 && (!isLastItem.value || props.hasMore)
);
const isActive = computed(() => isOpen.value || props.showNavigation);

watch(
	[currentItem, currentItemIndex],
	([item, itemIndex]) => {
		emit('current-item-change', item ?? null, itemIndex);
	},
	{ immediate: true, flush: 'sync' }
);

watch(currentItem, item => {
	if (item?.videoUrl) {
		currentBgImageUrl.value = null;
	} else {
		currentBgImageUrl.value = item?.images?.[0] ?? item?.thumbnailUrl ?? null;
	}
});
const isDragEnabled = computed(() => !props.disableScroll);

const {
	hasCurrentVideoLoaded,
	hasCurrentVideoBuffered,
	isGlobalMuted,
	isTapPaused,
	isVideoEffectivelyPaused,
	resetCurrentVideoState,
	toggleGlobalMute,
	setGlobalMuted,
	handleVideoTap,
	handleVideoLoaded: handleVideoLoadedState,
	handleVideoPlaying: handleVideoPlayingState,
	handleVideoBuffered: handleVideoBufferedState
} = useShortsVideoState({
	initialMuted: computed(() => props.initialMuted),
	isExternallyPaused: computed(() => props.isPaused),
	onFirstVideoLoaded: () => emit('first-video-loaded')
});

const { prefetchUpcoming, abortCurrentPrefetch, cleanupPrefetch } =
	useShortsPrefetch({
		getItems: () => props.items
	});

const debouncedPrefetchUpcoming = useDebounceFn(
	prefetchUpcoming,
	150
) as ReturnType<typeof useDebounceFn> & { cancel(): void };

const iosSharedVideo = provideIOSSharedVideo();

const shouldShowLoadingSkeleton = computed(() => {
	// Don't show skeleton if the Carousel is already rendered (isReady && items.length > 0)
	// The Carousel renders the videos even if they are still loading
	const carouselRendered =
		props.isReady && (props.items.length > 0 || hasLeadingItem.value);
	if (carouselRendered) {
		// If the Carousel is rendered, don't show skeleton
		// The individual videos will show their own loading states
		return false;
	}

	// Should show loading skeleton when:
	// 1. Data is not ready
	if (!props.isReady) return true;

	// 2. Is loading and there are no items yet
	if (props.isLoading && props.items.length === 0) return true;

	return false;
});

// Detectar mobile (só no client)
const isMobileDevice = ref(true);
let unwatchBreakpoints: (() => void) | null = null;

onMounted(() => {
	if (import.meta.client) {
		const breakpoints = useBreakpoints(breakpointsTailwind);
		isMobileDevice.value = breakpoints.smaller('lg').value;
		unwatchBreakpoints = watch(breakpoints.smaller('lg'), val => {
			isMobileDevice.value = val;
		});
	}
});

let isScrolling = false;
// After the cooldown expires, macOS trackpad may still be generating
// OS-level momentum (inertia) events.  We enter a "momentum phase" that
// blocks new navigations until wheel events stop arriving for at least
// MOMENTUM_IDLE_MS — a reliable signal that the gesture truly ended.
// MAX_SCROLL_BLOCK_MS caps the total block time (cooldown + momentum) so
// continuous intentional scrolling is never locked out indefinitely.
let isMomentumPhase = false;
let lastWheelTime = 0;
let lastNavigationTime = 0;
let lastNavigationDirection: number | null = null;
const MOMENTUM_IDLE_MS = 150;
const MAX_SCROLL_BLOCK_MS = 800;

const { start: startScrollCooldown, stop: stopScrollCooldown } = useTimeoutFn(
	() => {
		isScrolling = false;
		accumulatedDelta = 0;
		lastNavigationDirection = null;
		isMomentumPhase = true;
	},
	500,
	{ immediate: false }
);

function shouldLoadIndex(index: number): boolean {
	if (props.preloadFirst && index === 0) {
		return true;
	}

	if (!isActive.value) {
		return false;
	}

	const current = currentItemIndex.value;
	if (current < 0) {
		return index === 0;
	}

	const diff = index - current;
	const distance = Math.abs(diff);
	const isForward = diff > 0;

	// Current item: always load
	if (distance === 0) {
		return true;
	}

	// Adjacent (distance 1): load after current video's first segment
	// to give it full bandwidth priority for the critical first frame.
	// CacheStorage (shared between prefetch and Shaka's scheme handler)
	// mitigates the destroy→recreate cost on scroll — manifests and segments
	// are served from cache so player.load() resolves near-instantly.
	if (distance === 1) {
		return hasCurrentVideoLoaded.value;
	}

	// Second next video (distance 2 forward): load after current has buffered
	if (isForward && distance === 2) {
		return hasCurrentVideoBuffered.value;
	}

	return false;
}

/** Items at distance > 0 that are loading should use lighter preload config */
function isPreloadOnlyIndex(index: number): boolean {
	if (currentItemIndex.value < 0) return false;
	return index !== currentItemIndex.value && shouldLoadIndex(index);
}

function setupEmblaApi(api: CarouselApi) {
	if (!api) return;

	emblaApi.value = api;

	const onSelect = () => {
		const newIndex = api.selectedScrollSnap();
		if (newIndex !== currentIndex.value) {
			resetCurrentVideoState();
			currentIndex.value = newIndex;
			emit('index-change', newIndex, currentItemIndex.value);

			debouncedPrefetchUpcoming(currentItemIndex.value);
		}
	};

	const onReInit = () => {
		const snapIndex = api.selectedScrollSnap();
		if (
			snapIndex !== currentIndex.value &&
			currentIndex.value < api.scrollSnapList().length
		) {
			api.scrollTo(currentIndex.value, true);
		}
	};

	const onSettle = () => {
		// Animation finished — but do NOT unlock wheel scrolling here.
		// The cooldown timer is the primary gatekeeper that prevents
		// macOS trackpad momentum events from triggering extra navigations.
		// With a short animation duration (~30ms), resetting here would
		// bypass the cooldown almost immediately.
	};

	api.on('select', onSelect);
	api.on('reInit', onReInit);
	api.on('settle', onSettle);

	emblaListeners.value.push(
		{ event: 'select', callback: onSelect },
		{ event: 'reInit', callback: onReInit },
		{ event: 'settle', callback: onSettle }
	);

	if (props.initialIndex > 0) {
		api.scrollTo(props.initialIndex, true);
	}
}

function goToNext() {
	if (!canGoNext.value || !emblaApi.value) return;
	emblaApi.value.scrollNext(false); // false = animate (don't jump)
}

function goToPrevious() {
	if (!emblaApi.value) return;

	if (currentIndex.value <= 0) {
		emit('request-history');
		return;
	}

	emblaApi.value.scrollPrev(false);
}

function handleKeydown(event: KeyboardEvent) {
	if (!isActive.value || props.disableKeyboard) return;

	switch (event.key) {
		case 'Escape':
			handleClose('escape');
			break;
		case 'ArrowDown':
		case 'ArrowRight':
			goToNext();
			break;
		case 'ArrowUp':
		case 'ArrowLeft':
			goToPrevious();
			break;
	}
}

function handleClose(reason: PropertyReelsCloseReason = 'ui') {
	isOpen.value = false;
	emit('close', reason);
}

function handlePropertyClicked(property: ReelItemProperty) {
	emit('property-clicked', property);
	handleClose();
}

// Accumulated wheel delta for smoother scroll detection
let accumulatedDelta = 0;
const WHEEL_THRESHOLD = 80; // Higher threshold for more intentional scrolls

const reelContainerRef = ref<HTMLElement | null>(null);
const reelFrameAreaRef = ref<HTMLElement | null>(null);
const reelFrameSize = ref<QuantizedReelFrameSize | null>(null);
const shouldUseReelFrameFallback = computed(
	() =>
		isMobileDevice.value || !reelFrameSize.value || reelFrameSize.value.k === 1
);
const reelFrameStyle = computed(() => {
	if (shouldUseReelFrameFallback.value || !reelFrameSize.value) return;

	return {
		width: `${reelFrameSize.value.width}px`,
		height: `${reelFrameSize.value.height}px`
	};
});
const aboveFeedFrameStyle = computed(() => {
	if (shouldUseReelFrameFallback.value || !reelFrameSize.value) return;

	return {
		'--shorts-reel-frame-width': `${reelFrameSize.value.width}px`
	};
});

function updateReelFrameSize(availableWidth: number, availableHeight: number) {
	reelFrameSize.value = quantizeReelFrameSize(availableWidth, availableHeight);
}

useResizeObserver(reelFrameAreaRef, entries => {
	const entry = entries[0];
	if (!entry) return;

	updateReelFrameSize(entry.contentRect.width, entry.contentRect.height);
});

const handleGlobalWheel = (event: WheelEvent) => {
	if (!isActive.value || props.disableScroll) return;

	const target = event.target as HTMLElement;
	const scrollableParent = target.closest('[data-scrollable]');
	if (scrollableParent) return;

	const isInsideReel = reelContainerRef.value?.contains(target);
	if (!isInsideReel) return;

	// Always prevent native scroll inside the reel, even during the cooldown
	// window — otherwise unhandled events can propagate to the browser.
	event.preventDefault();
	event.stopPropagation();

	const now = performance.now();

	if (isScrolling) {
		lastWheelTime = now;
		return;
	}

	// After the cooldown expires, macOS trackpad may still be generating
	// momentum events.  Exit momentum phase when either:
	//   1. No wheel events arrived for MOMENTUM_IDLE_MS (gesture ended), or
	//   2. Total block time since last navigation exceeds MAX_SCROLL_BLOCK_MS
	//      (user is intentionally scrolling again — don't lock them out).
	if (isMomentumPhase) {
		const timeSinceNavigation = now - lastNavigationTime;
		if (
			timeSinceNavigation > MAX_SCROLL_BLOCK_MS ||
			now - lastWheelTime > MOMENTUM_IDLE_MS
		) {
			isMomentumPhase = false;
		} else {
			lastWheelTime = now;
			return;
		}
	}

	lastWheelTime = now;

	accumulatedDelta += event.deltaY;

	if (Math.abs(accumulatedDelta) > WHEEL_THRESHOLD) {
		const direction = accumulatedDelta > 0 ? 1 : -1;

		// Prevent consecutive scrolls in the same direction within the cooldown window
		if (lastNavigationDirection === direction) {
			accumulatedDelta = 0;
			return;
		}

		isScrolling = true;
		lastNavigationTime = now;
		lastNavigationDirection = direction;

		if (direction > 0) {
			goToNext();
		} else {
			goToPrevious();
		}

		accumulatedDelta = 0;
		stopScrollCooldown();
		startScrollCooldown();
	}
};

useEventListener(window, 'keydown', handleKeydown);
useEventListener(reelContainerRef, 'wheel', handleGlobalWheel, {
	passive: false
});

// On every touchend (valid gesture), re-bless the shared video element.
// Safari keeps the play permission after src changes, but Chrome iOS
// (WKWebView) resets it. Calling play() inside touchend renews it.
if (isIOSDevice) {
	useEventListener(reelContainerRef, 'touchend', () => {
		// Don't rebless (which calls play()) when the video was manually paused
		if (!isTapPaused.value) {
			iosSharedVideo.rebless();
		}
	});
}

function notifyExternalMedia() {
	if (!import.meta.client) return;
	window.dispatchEvent(new CustomEvent('reels-modal-opened'));
}

function handleVideoLoaded(index: number) {
	handleVideoLoadedState(index, currentItemIndex.value);
}

function handleVideoPlaying(index: number) {
	handleVideoPlayingState(index, currentItemIndex.value);
}

function handleVideoBuffered(index: number) {
	handleVideoBufferedState(index, currentItemIndex.value);
}

useShortsPageLifecycle({
	isOpen,
	initialIndex: toRef(props, 'initialIndex'),
	initialMuted: toRef(props, 'initialMuted'),
	showNavigation: toRef(props, 'showNavigation'),
	emblaApi,
	currentIndex,
	isScrollLocked,
	resetCurrentVideoState,
	setGlobalMuted,
	prefetchUpcoming,
	abortCurrentPrefetch: () => {
		debouncedPrefetchUpcoming.cancel();
		abortCurrentPrefetch();
	},
	cleanupPrefetch,
	notifyExternalMedia,
	onClose: () => {
		if (import.meta.client) {
			window.dispatchEvent(new CustomEvent('reels-modal-closed'));
		}
	}
});

// Adjust internal index when items are added or replaced
watch(
	() => props.items,
	(newItems, oldItems) => {
		if (!isOpen.value || !emblaApi.value) return;

		const oldLen = oldItems?.length ?? 0;
		const firstItemChanged =
			newItems[0]?.property.commercialId !==
			oldItems?.[0]?.property.commercialId;

		if (!firstItemChanged) return;

		const oldFirstId = oldItems?.[0]?.property.commercialId;
		const isPrepend =
			!!oldFirstId &&
			newItems.some(item => item.property.commercialId === oldFirstId);

		if (isPrepend) {
			// History was prepended — shift index so we stay on the same item
			const shift = newItems.length - oldLen;
			currentIndex.value += shift;
		} else {
			// Completely different content (e.g. filter change) — reset
			currentIndex.value = props.initialIndex;
			nextTick(() => {
				emblaApi.value?.scrollTo(props.initialIndex, true);
			});
		}
	}
);

// Update Embla drag when disableScroll changes
watch(
	() => props.disableScroll,
	disabled => {
		if (emblaApi.value) {
			// Embla's reInit with new options
			emblaApi.value.reInit({ watchDrag: !disabled });
		}
	}
);

watch(
	currentIndex,
	() => {
		const itemIndex = currentItemIndex.value;
		if (itemIndex < 0) return;

		const itemsFromEnd = totalItems.value - itemIndex - 1;
		if (itemsFromEnd <= 3 && props.hasMore) {
			emit('load-more');
		}
	},
	{ flush: 'post' }
);

onBeforeUnmount(() => {
	stopScrollCooldown();
	if (unwatchBreakpoints) {
		unwatchBreakpoints();
	}
	// Clean up Embla listeners
	if (emblaApi.value) {
		emblaListeners.value.forEach(({ event, callback }) => {
			emblaApi.value?.off(event, callback);
		});
	}
	emblaListeners.value = [];
});
</script>

<template>
	<Teleport to="body">
		<Transition>
			<div
				v-if="isOpen"
				ref="reelContainerRef"
				:class="[
					'fixed inset-0 z-100 flex items-center justify-center',
					{ 'bg-black/95': !$slots.background && !currentBgImageUrl }
				]"
				data-testid="property-reels-feed"
				data-reels-modal
				@click.self="handleClose('backdrop')"
			>
				<!-- Desktop blurred image background for image-only reels -->
				<Transition
					enter-active-class="transition-opacity duration-400"
					enter-from-class="opacity-0"
					enter-to-class="opacity-100"
					leave-active-class="transition-opacity duration-400"
					leave-from-class="opacity-100"
					leave-to-class="opacity-0"
				>
					<div
						v-if="currentBgImageUrl && !isMobileDevice"
						:key="currentBgImageUrl"
						class="pointer-events-none absolute inset-0 overflow-hidden"
					>
						<NuxtImg
							:src="currentBgImageUrl"
							format="webp"
							:quality="bgImageQuality"
							width="960"
							densities="x1"
							loading="lazy"
							class="size-full object-cover blur-2xl scale-110 opacity-60"
							aria-hidden="true"
						/>
						<div class="absolute inset-0 bg-black/60" />
					</div>
				</Transition>

				<div
					class="relative flex h-full w-full items-center justify-center [--shorts-reel-max-width:483px] md:h-dvh md:pb-12 md:px-4 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,var(--shorts-reel-max-width))_minmax(0,1fr)] lg:items-stretch lg:px-6 xl:px-12 xl:gap-8 2xl:gap-9"
				>
					<div
						class="hidden lg:flex lg:items-end lg:justify-end lg:self-stretch"
					>
						<slot
							v-if="currentItem"
							name="left-sidebar"
							:current-item="currentItem"
							:current-index="currentIndex"
						/>
					</div>

					<div
						class="flex h-full w-full min-h-0 flex-col items-stretch md:gap-3 lg:gap-0 xl:gap-4 xl:pt-3.5 lg:justify-self-center"
					>
						<div
							v-if="$slots['above-feed']"
							class="w-full shrink-0 md:self-center md:w-[min(var(--shorts-reel-frame-width,var(--shorts-reel-max-width)),100%)]"
							data-testid="shorts-reel-above-feed-frame"
							:style="aboveFeedFrameStyle"
						>
							<slot name="above-feed" />
						</div>

						<div
							ref="reelFrameAreaRef"
							class="relative flex min-h-0 flex-1 items-center justify-center"
						>
							<div
								:class="[
									'relative shrink-0',
									{
										'h-full w-full md:aspect-[9/16] md:h-auto md:max-h-full md:w-[min(var(--shorts-reel-max-width),100%)]':
											shouldUseReelFrameFallback
									}
								]"
								data-testid="shorts-reel-frame"
								:style="reelFrameStyle"
							>
								<ShortsVideoFeed
									:items="items"
									:is-ready="isReady"
									:is-loading="isLoading"
									:current-index="currentIndex"
									:is-active="isActive"
									:autoplay-armed="autoplayArmed"
									:is-video-effectively-paused="isVideoEffectivelyPaused"
									:is-global-muted="isGlobalMuted"
									:is-mobile-device="isMobileDevice"
									:video-type="videoType"
									:is-drag-enabled="isDragEnabled"
									:hide-header-controls="hideHeaderControls"
									:has-leading-item="hasLeadingItem"
									:leading-item-count="leadingItemCount"
									:should-show-loading-skeleton="shouldShowLoadingSkeleton"
									:empty-state-title="emptyStateTitle"
									:empty-state-description="emptyStateDescription"
									:empty-state-action-label="emptyStateActionLabel"
									:should-load-index="shouldLoadIndex"
									:is-preload-only-index="isPreloadOnlyIndex"
									:is-tap-paused="isTapPaused"
									@init-api="setupEmblaApi"
									@property-clicked="handlePropertyClicked"
									@video-loaded="handleVideoLoaded"
									@video-playing="handleVideoPlaying"
									@video-buffered="handleVideoBuffered"
									@tap="
										() => {
											handleVideoTap();
											emit('tap');
										}
									"
									@toggle-mute="toggleGlobalMute"
									@close="handleClose"
									@empty-state-action="emit('empty-state-action')"
									@carousel-image-change="handleCarouselImageChange"
								>
									<template #desktop-mode-toggle>
										<slot name="desktop-mode-toggle" />
									</template>

									<template #mobile-bottom-sheet>
										<slot name="mobile-bottom-sheet" />
									</template>

									<template v-if="hasLeadingItem" #leading-item>
										<slot name="leading-item" />
									</template>
								</ShortsVideoFeed>
							</div>
						</div>
					</div>

					<ShortsDesktopActions
						:can-go-previous="canGoPrevious"
						:can-go-next="canGoNext"
						@go-previous="goToPrevious"
						@go-next="goToNext"
					>
						<slot
							v-if="currentItem"
							name="right-sidebar"
							:current-item="currentItem"
							:current-index="currentIndex"
						/>
					</ShortsDesktopActions>
				</div>
				<div
					v-if="$slots.background"
					class="pointer-events-none absolute inset-0 -z-1 overflow-hidden"
				>
					<slot name="background" />
				</div>
			</div>
		</Transition>
	</Teleport>
</template>
